import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Compass,
  Check,
  Sparkles,
  Shield,
  Users,
  FileText,
  QrCode,
  BarChart3,
  Mail,
  Zap,
  Crown,
  ArrowRight,
  Loader2,
  Star,
  Building2,
  Award,
  Globe2,
  Lock,
  HeartHandshake,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_ICONS = {
  free: Sparkles,
  pro: Crown,
  grande_oriente: Award,
};

const PLAN_ACCENTS = {
  free: {
    ring: "ring-slate-700/60",
    border: "border-slate-700/60",
    badge: "bg-slate-800 text-slate-300",
    cta: "bg-slate-800 hover:bg-slate-700 text-slate-100",
    iconBg: "bg-slate-800/60 text-slate-300",
  },
  pro: {
    ring: "ring-[#D4AF37]",
    border: "border-[#D4AF37]",
    badge: "bg-[#D4AF37] text-slate-950",
    cta: "bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950",
    iconBg: "bg-[#D4AF37]/15 text-[#D4AF37]",
  },
  grande_oriente: {
    ring: "ring-purple-500/50",
    border: "border-purple-700/60",
    badge: "bg-purple-900/40 text-purple-200 border border-purple-700/60",
    cta: "bg-purple-700 hover:bg-purple-600 text-white",
    iconBg: "bg-purple-900/40 text-purple-300",
  },
};

const BENEFITS = [
  {
    icon: Users,
    title: "Quadro de Obreiros Digital",
    desc: "Cadastro completo com grau, cargo, CIM, foto e histórico de presença por Irmão.",
  },
  {
    icon: FileText,
    title: "Balaústres com Assinatura Digital",
    desc: "Geração automática do balaústre, papel timbrado personalizado e envio por e-mail.",
  },
  {
    icon: QrCode,
    title: "Check-in por QR Code",
    desc: "Cada sessão tem seu QR — o Irmão registra presença pelo celular em segundos.",
  },
  {
    icon: BarChart3,
    title: "Tesouraria e Balancete",
    desc: "Centros de custo, mensalidades, descontos de pontualidade e extrato por Irmão.",
  },
  {
    icon: Mail,
    title: "Boletim e Convocação",
    desc: "Envio de convocação e boletim para todo o Quadro em um clique.",
  },
  {
    icon: Shield,
    title: "Trilha de Auditoria",
    desc: "Todo lançamento e aprovação é registrado. Transparência total para a Loja.",
  },
];

const FAQ = [
  {
    q: "Preciso instalar alguma coisa?",
    a: "Não. Lojas Integradas roda 100% no navegador. Basta cadastrar sua Loja e começar a usar em minutos.",
  },
  {
    q: "Meus dados ficam seguros?",
    a: "Sim. Cada Loja é isolada em um tenant próprio, com autenticação por Irmão e trilha de auditoria completa. Backups automáticos diários.",
  },
  {
    q: "Posso mudar de plano depois?",
    a: "Pode. É possível fazer upgrade ou downgrade a qualquer momento — a cobrança é ajustada de forma proporcional.",
  },
  {
    q: "Como funciona o pagamento?",
    a: "Cobrança recorrente mensal ou anual via Stripe (cartão de crédito internacional). Todos os cartões principais são aceitos.",
  },
  {
    q: "E se eu não gostar?",
    a: "Você pode cancelar a qualquer momento direto do painel. Sem multa, sem burocracia.",
  },
  {
    q: "O plano Ouro serve pra minha Potência?",
    a: "O plano Ouro entrega recursos executivos avançados: dashboard consolidado com KPIs estratégicos, API para integrações, SLA garantido e gerente de conta dedicado. Ideal pra Lojas grandes ou Potências que precisam do máximo do sistema.",
  },
];

const TESTIMONIALS = [
  {
    name: "Ir. Ricardo M.",
    office: "Venerável Mestre — Loja Fraternidade nº 42",
    quote:
      "Antes eu perdia horas fechando o balaústre no Word. Agora está pronto ao fim da sessão, com assinatura do Secretário. Mudou a Loja.",
  },
  {
    name: "Ir. Antônio C.",
    office: "Tesoureiro — ARLS Estrela do Sul",
    quote:
      "O extrato por Irmão e o balancete automático acabaram com a discussão de mensalidades. Cada centavo rastreado.",
  },
  {
    name: "Ir. Paulo H.",
    office: "Grão-Mestre Adjunto — Grande Oriente Estadual",
    quote:
      "Consegui ver a saúde de 38 Lojas em um único painel. Impossível voltar pro papel.",
  },
];

export const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState("monthly"); // monthly | yearly
  const [processing, setProcessing] = useState(null); // plan_id being processed
  const [currentLoja, setCurrentLoja] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [plansRes] = await Promise.all([axios.get(`${API}/pricing/plans`)]);
        setPlans(plansRes.data.plans || []);
        try {
          const lojaRes = await axios.get(`${API}/lojas/mine`, { withCredentials: true });
          setCurrentLoja(lojaRes.data);
        } catch {
          setCurrentLoja(null);
        }
      } catch (e) {
        console.error(e);
        toast.error("Erro ao carregar planos.");
      } finally {
        setLoading(false);
      }
    })();

    if (searchParams.get("canceled") === "1") {
      toast.info("Checkout cancelado. Sua Loja não foi cobrada.");
    }
    // navigate/axios/toast/API são estáveis (módulo).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSubscribe = async (plan) => {
    if (plan.id === "free") {
      // Free: manda para signup
      navigate("/signup");
      return;
    }

    const lookupKey =
      billing === "yearly" ? plan.lookup_key_yearly : plan.lookup_key_monthly;

    if (!lookupKey) {
      toast.error("Plano indisponível.");
      return;
    }

    // Se não estiver logado ou sem loja, manda pro signup guardando o plano
    if (!currentLoja) {
      // sessionStorage é preferível a localStorage: escopo por aba e sem persistência entre sessões.
      // Valor guardado é um identificador público (lookup_key), não credencial.
      try {
        sessionStorage.setItem("pending_plan_lookup", lookupKey);
      } catch { /* storage bloqueado — segue com query param apenas */ }
      toast.info("Cadastre sua Loja primeiro. Vamos redirecionar.");
      setTimeout(() => navigate(`/signup?plan=${lookupKey}`), 800);
      return;
    }

    setProcessing(plan.id);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        loja_id: currentLoja.id,
        email: currentLoja.contact_email || currentLoja.loja_email,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error("Sem URL de checkout.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Erro ao iniciar checkout.");
      setProcessing(null);
    }
  };

  const priceOf = (plan) => {
    if (plan.price_monthly_brl === 0) return { amount: 0, cycle: "" };
    if (billing === "yearly") {
      return {
        amount: Math.round(plan.price_yearly_brl / 12),
        cycle: "/mês",
        note: `Cobrado R$ ${plan.price_yearly_brl.toLocaleString("pt-BR")} anual`,
      };
    }
    return {
      amount: plan.price_monthly_brl,
      cycle: "/mês",
      note: "Cobrado mensalmente",
    };
  };

  return (
    <div className="min-h-screen bg-[#080C14] text-[#F8FAFC] selection:bg-[#D4AF37] selection:text-slate-950 font-sans">
      {/* Grain texture overlay */}
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.04] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' /%3E%3C/svg%3E\")",
        }}
      />

      {/* Top Nav */}
      <nav className="relative z-10 border-b border-slate-800/60 backdrop-blur-md bg-[#080C14]/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <button
            data-testid="pricing-brand-link"
            onClick={() => navigate("/")}
            className="flex items-center gap-3 group"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B6914] flex items-center justify-center shadow-lg shadow-[#D4AF37]/20 group-hover:scale-105 transition-transform">
              <Compass className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
            </div>
            <div className="text-left">
              <div className="text-sm font-serif font-bold text-white leading-tight">
                Lojas Integradas
              </div>
              <div className="text-[10px] tracking-widest uppercase text-[#D4AF37]">
                Gestão Maçônica
              </div>
            </div>
          </button>

          <div className="flex items-center gap-2 sm:gap-4">
            <button
              data-testid="pricing-nav-features"
              onClick={() => navigate("/features")}
              className="hidden sm:block text-sm text-slate-300 hover:text-[#D4AF37] transition-colors"
            >
              Recursos
            </button>
            <button
              data-testid="pricing-nav-blog"
              onClick={() => navigate("/blog")}
              className="hidden sm:block text-sm text-slate-300 hover:text-[#D4AF37] transition-colors"
            >
              Blog
            </button>
            <button
              data-testid="pricing-nav-signup"
              onClick={() => navigate("/signup")}
              className="hidden sm:block text-sm text-slate-300 hover:text-[#D4AF37] transition-colors"
            >
              Cadastrar Loja
            </button>
            <button
              data-testid="pricing-nav-login"
              onClick={() => navigate("/")}
              className="text-sm px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 transition-colors"
            >
              Entrar
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-14 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-1.5 text-xs uppercase tracking-widest text-[#D4AF37] mb-6">
          <Sparkles className="w-3.5 h-3.5" />
          Planos e Assinatura
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white leading-[1.1] max-w-4xl mx-auto">
          A gestão da sua Loja
          <br />
          <span className="text-[#D4AF37]">merece ordem e beleza.</span>
        </h1>

        <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
          Uma plataforma completa para o Venerável, Secretaria, Tesouraria e todos os Irmãos.
          Balaústres, presença por QR, tesouraria transparente e auditoria — sem planilhas, sem papel perdido.
        </p>

        {/* Trust badges */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 text-[#D4AF37]" />
            Dados isolados por Loja
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-[#D4AF37]" />
            Trilha de auditoria completa
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-[#D4AF37]" />
            Pronto em 5 minutos
          </div>
          <div className="flex items-center gap-2">
            <HeartHandshake className="w-3.5 h-3.5 text-[#D4AF37]" />
            Cancelamento livre
          </div>
        </div>
      </section>

      {/* Billing toggle */}
      <section className="relative z-10 max-w-md mx-auto px-4 mb-10">
        <div
          data-testid="pricing-billing-toggle"
          className="flex items-center bg-slate-900/60 border border-slate-800 rounded-full p-1"
        >
          <button
            data-testid="pricing-billing-monthly"
            onClick={() => setBilling("monthly")}
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all ${
              billing === "monthly"
                ? "bg-[#D4AF37] text-slate-950 shadow"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            Mensal
          </button>
          <button
            data-testid="pricing-billing-yearly"
            onClick={() => setBilling("yearly")}
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all relative ${
              billing === "yearly"
                ? "bg-[#D4AF37] text-slate-950 shadow"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            Anual
            <span className="absolute -top-2 -right-2 text-[9px] bg-emerald-500 text-slate-950 px-1.5 py-0.5 rounded-full font-bold">
              -20%
            </span>
          </button>
        </div>
      </section>

      {/* Plans */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {plans.map((plan) => {
              const Icon = PLAN_ICONS[plan.id] || Sparkles;
              const accent = PLAN_ACCENTS[plan.id] || PLAN_ACCENTS.free;
              const price = priceOf(plan);
              const isProcessing = processing === plan.id;

              return (
                <div
                  key={plan.id}
                  data-testid={`plan-card-${plan.id}`}
                  className={`relative rounded-2xl bg-[#0d1420] border ${accent.border} p-8 flex flex-col transition-all hover:-translate-y-1 hover:shadow-2xl ${
                    plan.highlighted
                      ? "ring-2 " + accent.ring + " md:scale-[1.03] shadow-2xl shadow-[#D4AF37]/10"
                      : ""
                  }`}
                >
                  {plan.highlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className={`text-[10px] tracking-widest uppercase font-bold px-4 py-1.5 rounded-full ${accent.badge}`}>
                        Mais escolhido
                      </span>
                    </div>
                  )}

                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${accent.iconBg}`}>
                    <Icon className="w-6 h-6" strokeWidth={2} />
                  </div>

                  <h3 className="mt-5 text-2xl font-serif font-bold text-white">{plan.name}</h3>
                  <p className="text-sm text-slate-400 mt-1">{plan.tagline}</p>

                  <div className="mt-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-sm text-slate-400">R$</span>
                      <span className="text-5xl font-serif font-bold text-white tracking-tight">
                        {price.amount}
                      </span>
                      {price.cycle && (
                        <span className="text-sm text-slate-500">{price.cycle}</span>
                      )}
                    </div>
                    {price.note && (
                      <p className="text-[11px] text-slate-500 mt-1">{price.note}</p>
                    )}
                  </div>

                  <button
                    data-testid={`plan-cta-${plan.id}`}
                    onClick={() => handleSubscribe(plan)}
                    disabled={isProcessing}
                    className={`mt-6 w-full py-3 rounded-xl font-bold text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${accent.cta} disabled:opacity-60`}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Processando...
                      </>
                    ) : plan.id === "free" ? (
                      <>
                        Começar Grátis <ArrowRight className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        Assinar {plan.name} <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  <div className="mt-8 space-y-3 flex-1">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                      Inclui
                    </div>
                    {plan.features.map((f, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" strokeWidth={3} />
                        <span className="text-sm text-slate-300 leading-relaxed">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-center text-xs text-slate-500 mt-8">
          Todos os pagamentos são processados de forma segura via Stripe. Sem fidelidade. Cancele quando quiser.
        </p>
      </section>

      {/* Benefits Grid */}
      <section className="relative z-10 border-t border-slate-800/60 py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-widest uppercase text-[#D4AF37] font-bold mb-4">
              <Star className="w-3 h-3 fill-current" />
              Tudo o que sua Loja precisa
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white max-w-3xl mx-auto leading-tight">
              Uma ferramenta pensada por Maçons,
              <br />
              <span className="text-[#D4AF37]">para Maçons.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div
                  key={i}
                  data-testid={`benefit-card-${i}`}
                  className="rounded-2xl bg-[#0d1420]/60 border border-slate-800/60 p-6 hover:border-[#D4AF37]/30 hover:-translate-y-0.5 transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <h3 className="mt-4 text-lg font-serif font-bold text-white">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative z-10 border-t border-slate-800/60 py-24 bg-gradient-to-b from-transparent to-[#0a0f1a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-widest uppercase text-[#D4AF37] font-bold mb-4">
              <HeartHandshake className="w-3 h-3" />
              Depoimentos
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white">
              Aprovado pelos Irmãos
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="rounded-2xl bg-[#0d1420] border border-slate-800/60 p-7 flex flex-col"
              >
                <div className="flex gap-1 text-[#D4AF37] mb-4">
                  {[...Array(5)].map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-slate-300 leading-relaxed text-sm italic flex-1">
                  “{t.quote}”
                </p>
                <div className="mt-6 pt-4 border-t border-slate-800/60">
                  <div className="text-sm font-semibold text-white">{t.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.office}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 border-t border-slate-800/60 py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white">
              Perguntas Frequentes
            </h2>
            <p className="text-slate-400 mt-3 text-sm">
              Ainda tem dúvida? Fale com a gente pelo formulário de cadastro.
            </p>
          </div>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <details
                key={i}
                data-testid={`faq-${i}`}
                className="group rounded-xl bg-[#0d1420] border border-slate-800/60 p-5 open:border-[#D4AF37]/30 transition-colors"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                  <span className="text-base font-semibold text-white">{item.q}</span>
                  <span className="w-6 h-6 rounded-full bg-slate-800 text-[#D4AF37] flex items-center justify-center text-lg font-bold group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 border-t border-slate-800/60 py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Building2 className="w-14 h-14 text-[#D4AF37] mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white leading-tight">
            Digitalize sua Loja hoje.
            <br />
            <span className="text-[#D4AF37]">Sem instalação. Sem burocracia.</span>
          </h2>
          <p className="mt-5 text-slate-400 max-w-xl mx-auto text-sm">
            Comece grátis. Faça o upgrade quando sentir necessidade. Seus Irmãos merecem.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              data-testid="cta-start-free"
              onClick={() => navigate("/signup")}
              className="w-full sm:w-auto px-8 py-3.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold text-sm uppercase tracking-wider rounded-xl shadow-lg shadow-[#D4AF37]/20 transition-all inline-flex items-center justify-center gap-2"
            >
              Cadastrar minha Loja <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="cta-scroll-plans"
              onClick={() => window.scrollTo({ top: 800, behavior: "smooth" })}
              className="w-full sm:w-auto px-8 py-3.5 bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold text-sm uppercase tracking-wider rounded-xl transition-all"
            >
              Ver planos
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-800/60 py-8 px-4 text-center text-xs text-slate-500">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="flex items-center gap-2">
            <Globe2 className="w-3.5 h-3.5" /> Lojas Integradas • Plataforma SaaS Maçônica
          </span>
          <span className="font-mono text-[#D4AF37]">
            Liberdade • Igualdade • Fraternidade
          </span>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
